/** The account's channels as the composer offers them: ordered, labelled, and matched to the channel a loaded thread arrived on. */

import type { Channel } from '@/types';

/**
 * Composer order. Email leads because it is the channel most tickets arrive on
 * and the one a fresh simulator starts on; names an account adds beyond these
 * keep the order the API gave them.
 */
const COMPOSER_ORDER = ['email', 'chat', 'social', 'sms'];

/** Names a plain capitalisation gets wrong. */
const LABEL_OVERRIDES: Record<string, string> = { sms: 'SMS' };

/**
 * Chatwoot channel types as the conversation API reports them (its `Channel::`
 * prefix already stripped), mapped onto the account's own channel names.
 */
const THREAD_CHANNELS: Record<string, string> = {
	email: 'email',
	webwidget: 'chat',
	api: 'chat',
	sms: 'sms',
	twiliosms: 'sms',
	facebookpage: 'social',
	instagram: 'social',
	twitterprofile: 'social',
	whatsapp: 'social',
	telegram: 'social',
	line: 'social'
};

export const channelKey = (channel: Channel): string => channel.name.trim().toLowerCase();

export const channelLabel = (channel: Channel): string => {
	const key = channelKey(channel);
	return LABEL_OVERRIDES[key] ?? key.charAt(0).toUpperCase() + key.slice(1);
};

const rank = (channel: Channel): number => {
	const index = COMPOSER_ORDER.indexOf(channelKey(channel));
	return index === -1 ? COMPOSER_ORDER.length : index;
};

/** Sort is stable, so channels outside the fixed order keep their incoming order. */
export const orderChannels = (channels: Channel[]): Channel[] => [...channels].sort((a, b) => rank(a) - rank(b));

export const defaultChannelId = (channels: Channel[]): string | undefined =>
	channels.find((channel) => channelKey(channel) === 'email')?.id ?? channels[0]?.id;

const normalizeThreadChannel = (value: string): string => {
	const [prefixed, suffix] = value.split('::');
	return (suffix ?? prefixed).replace(/[^a-z]/gi, '').toLowerCase();
};

/** The channel a loaded thread arrived on, or undefined when the account has no channel for it. */
export const channelIdForThread = (channels: Channel[], threadChannel: string | null): string | undefined => {
	if (!threadChannel) return undefined;
	const name = THREAD_CHANNELS[normalizeThreadChannel(threadChannel)];
	return name ? channels.find((channel) => channelKey(channel) === name)?.id : undefined;
};
